/**
 * performanceMonitor.ts
 * 
 * 性能监测工具，用于追踪应用性能和用户事件
 */

// 事件计数器
interface EventCounter {
  [eventName: string]: number;
}

// 实时事件统计
interface EventStats {
  [eventName: string]: {
    total: number;      // 总计数
    lastMinute: number; // 最近一分钟
    lastTenSec: number; // 最近十秒
  };
}

// 性能标记
interface PerformanceMark {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  timestamp?: number; // 记录完成时间
}

// 性能指标
interface PerformanceMetrics {
  fps: number;                        // 帧率
  memory?: number;                    // 内存使用(MB)
  eventCount: number;                 // 当前事件总数
  eventRate: number;                  // 事件频率(每秒)
  domNodes?: number;                  // DOM节点数
  resourceCount?: number;             // 资源数量
  jsHeapSizeLimit?: number;           // JS堆大小限制
  longTasks: number;                  // 长任务数(>50ms)
  responseTime: number;               // 平均响应时间
  interactionCount: number;           // 用户交互次数
  lastInteractionTime?: number;       // 上次交互时间
  lastGcTime?: number;                // 上次GC时间(如可获取)
  errors: number;                     // 错误数
  warnings: number;                   // 警告数
}

class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private eventCounter: EventCounter = {};
  private realtimeEvents: { name: string; timestamp: number }[] = [];
  private marks: Map<string, PerformanceMark> = new Map();
  private recentMarks: PerformanceMark[] = [];
  private isEnabled: boolean = true;
  private metrics: PerformanceMetrics = {
    fps: 0,
    eventCount: 0,
    eventRate: 0,
    longTasks: 0,
    responseTime: 0,
    interactionCount: 0,
    errors: 0,
    warnings: 0
  };
  
  // 用于计算事件频率的变量
  private eventTimestamps: number[] = [];
  private lastMetricsUpdate: number = Date.now();

  private constructor() {
    // 定期清理旧数据
    setInterval(() => this.cleanupOldData(), 10000);
    
    // 监听错误
    window.addEventListener('error', () => {
      this.metrics.errors++;
    });
    
    // 监听警告
    const originalWarn = console.warn;
    console.warn = (...args) => {
      this.metrics.warnings++;
      originalWarn.apply(console, args);
    };
    
    // 监听用户交互
    ['click', 'keydown', 'touchstart'].forEach(eventType => {
      window.addEventListener(eventType, () => {
        this.metrics.interactionCount++;
        this.metrics.lastInteractionTime = Date.now();
      });
    });
    
    // 使用PerformanceObserver监听长任务
    if (typeof PerformanceObserver === 'function') {
      try {
        const longTaskObserver = new PerformanceObserver(list => {
          list.getEntries().forEach(entry => {
            if (entry.duration > 50) { // 长任务定义为>50ms
              this.metrics.longTasks++;
            }
          });
        });
        
        longTaskObserver.observe({ entryTypes: ['longtask'] });
      } catch (e) {
        console.debug('PerformanceObserver for longTasks not supported');
      }
    }
  }

  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * 更新性能指标
   */
  public updateMetrics(fps?: number): void {
    if (fps !== undefined) {
      this.metrics.fps = fps;
    }
    
    // 计算事件频率
    const now = Date.now();
    const oneSecondAgo = now - 1000;
    
    // 只保留最近一秒的事件
    this.eventTimestamps = this.eventTimestamps
      .filter(timestamp => timestamp > oneSecondAgo);
    
    // 计算事件频率（每秒事件数）
    this.metrics.eventRate = this.eventTimestamps.length;
    
    // 更新当前事件总数
    this.metrics.eventCount = Object.values(this.eventCounter)
      .reduce((sum, count) => sum + count, 0);
    
    // 获取DOM节点数
    this.metrics.domNodes = document.querySelectorAll('*').length;
    
    // 获取资源数量
    if (performance && performance.getEntriesByType) {
      this.metrics.resourceCount = performance.getEntriesByType('resource').length;
    }
    
    // 获取内存使用情况
    if (performance && 'memory' in performance) {
      const memory = (performance as any).memory;
      if (memory) {
        this.metrics.memory = Math.round(memory.usedJSHeapSize / (1024 * 1024));
        this.metrics.jsHeapSizeLimit = Math.round(memory.jsHeapSizeLimit / (1024 * 1024));
      }
    }
    
    // 计算平均响应时间
    const completedMarks = this.recentMarks.filter(mark => mark.duration !== undefined);
    if (completedMarks.length > 0) {
      const sum = completedMarks.reduce((total, mark) => total + (mark.duration || 0), 0);
      this.metrics.responseTime = Math.round(sum / completedMarks.length);
    }
    
    this.lastMetricsUpdate = now;
  }

  /**
   * 获取当前性能指标
   */
  public getMetrics(): PerformanceMetrics {
    // 如果上次更新超过1秒，重新计算
    if (Date.now() - this.lastMetricsUpdate > 1000) {
      this.updateMetrics();
    }
    
    return { ...this.metrics };
  }

  /**
   * 启用或禁用性能监测
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * 清理旧数据
   */
  private cleanupOldData(): void {
    const now = Date.now();
    
    // 清理超过2分钟的实时事件
    this.realtimeEvents = this.realtimeEvents.filter(
      event => now - event.timestamp < 120000
    );
    
    // 清理超过5分钟的性能标记
    this.recentMarks = this.recentMarks.filter(
      mark => (mark.timestamp || 0) > now - 300000
    );
    
    // 清理超过1秒的事件时间戳
    this.eventTimestamps = this.eventTimestamps.filter(
      timestamp => now - timestamp < 1000
    );
  }

  /**
   * 记录事件发生
   * @param eventName 事件名称
   */
  public trackEvent(eventName: string): void {
    if (!this.isEnabled) return;
    
    // 更新总计数
    if (!this.eventCounter[eventName]) {
      this.eventCounter[eventName] = 0;
    }
    this.eventCounter[eventName]++;
    
    // 添加到实时事件列表
    this.realtimeEvents.push({
      name: eventName,
      timestamp: Date.now()
    });
    
    // 记录事件时间戳用于计算频率
    this.eventTimestamps.push(Date.now());
    
    // 可选：记录到控制台
    console.debug(`事件 [${eventName}] 触发，当前计数: ${this.eventCounter[eventName]}`);
  }

  /**
   * 获取事件计数
   * @param eventName 事件名称
   */
  public getEventCount(eventName: string): number {
    return this.eventCounter[eventName] || 0;
  }

  /**
   * 获取所有事件计数
   */
  public getAllEventCounts(): EventCounter {
    return { ...this.eventCounter };
  }

  /**
   * 获取实时事件统计数据
   * @returns 实时事件统计
   */
  public getRealtimeEventStats(): EventStats {
    const stats: EventStats = {};
    const now = Date.now();
    
    // 初始化所有已知事件
    Object.keys(this.eventCounter).forEach(event => {
      stats[event] = {
        total: this.eventCounter[event],
        lastMinute: 0,
        lastTenSec: 0
      };
    });
    
    // 计算最近一分钟和十秒的事件数
    this.realtimeEvents.forEach(event => {
      if (!stats[event.name]) {
        stats[event.name] = {
          total: this.eventCounter[event.name] || 0,
          lastMinute: 0,
          lastTenSec: 0
        };
      }
      
      const elapsedMs = now - event.timestamp;
      
      if (elapsedMs < 60000) { // 一分钟内
        stats[event.name].lastMinute += 1;
        
        if (elapsedMs < 10000) { // 十秒内
          stats[event.name].lastTenSec += 1;
        }
      }
    });
    
    return stats;
  }

  /**
   * 重置事件计数器
   * @param eventName 可选，指定要重置的事件名称
   */
  public resetEventCounts(eventName?: string): void {
    if (eventName) {
      this.eventCounter[eventName] = 0;
      this.realtimeEvents = this.realtimeEvents.filter(e => e.name !== eventName);
    } else {
      this.eventCounter = {};
      this.realtimeEvents = [];
    }
  }

  /**
   * 开始性能标记
   * @param markName 标记名称
   */
  public startMark(markName: string): void {
    if (!this.isEnabled) return;
    
    this.marks.set(markName, {
      name: markName,
      startTime: performance.now()
    });
  }

  /**
   * 结束性能标记并计算持续时间
   * @param markName 标记名称
   * @returns 持续时间（毫秒）
   */
  public endMark(markName: string): number | undefined {
    if (!this.isEnabled) return;
    
    const mark = this.marks.get(markName);
    if (!mark) {
      console.warn(`标记 [${markName}] 不存在`);
      return;
    }

    mark.endTime = performance.now();
    mark.duration = mark.endTime - mark.startTime;
    mark.timestamp = Date.now();
    
    // 将完成的标记添加到最近标记列表
    this.recentMarks.push({...mark});
    
    // 可选：记录到控制台
    console.debug(`性能标记 [${markName}] 完成，持续时间: ${mark.duration.toFixed(2)}ms`);
    
    return mark.duration;
  }

  /**
   * 获取标记信息
   * @param markName 标记名称
   */
  public getMark(markName: string): PerformanceMark | undefined {
    return this.marks.get(markName);
  }

  /**
   * 获取所有标记
   */
  public getAllMarks(): PerformanceMark[] {
    return Array.from(this.marks.values());
  }

  /**
   * 获取最近完成的性能标记
   * @param limit 限制返回数量
   */
  public getRecentMarks(limit: number = 10): PerformanceMark[] {
    return this.recentMarks
      .filter(mark => mark.duration !== undefined)
      .sort((a, b) => ((b.timestamp || 0) - (a.timestamp || 0)))
      .slice(0, limit);
  }

  /**
   * 获取性能标记的平均时间
   * @param markName 性能标记名称
   * @param timeWindow 时间窗口（毫秒），默认为最近5分钟
   */
  public getAverageMarkDuration(markName: string, timeWindow: number = 300000): number | null {
    const now = Date.now();
    const relevantMarks = this.recentMarks.filter(
      mark => mark.name === markName && 
              mark.duration !== undefined && 
              (mark.timestamp || 0) > now - timeWindow
    );
    
    if (relevantMarks.length === 0) return null;
    
    const sum = relevantMarks.reduce(
      (total, mark) => total + (mark.duration || 0), 
      0
    );
    
    return sum / relevantMarks.length;
  }

  /**
   * 清除所有标记
   */
  public clearMarks(): void {
    this.marks.clear();
    this.recentMarks = [];
  }

  /**
   * 记录错误
   * @param error 错误对象或消息
   */
  public trackError(error: Error | string): void {
    this.metrics.errors++;
    this.trackEvent('error');
    console.error(error);
  }

  /**
   * 记录警告
   * @param warning 警告消息
   */
  public trackWarning(warning: string): void {
    this.metrics.warnings++;
    this.trackEvent('warning');
    console.warn(warning);
  }
}

// 导出单例实例
export const performanceMonitor = PerformanceMonitor.getInstance(); 